type LightboxItem = {
	src: string;
	title?: string;
	type?: 'image' | 'video' | 'iframe';
};

export class Lightbox {
	private items: LightboxItem[] = [];
	private currentIndex = 0;
	private lightboxEl: HTMLElement | null = null;

	constructor() {
		document.addEventListener('click', (e) => {
			const target = (e.target as HTMLElement).closest<HTMLElement>('[data-lightbox]');
			if (target) {
				e.preventDefault();
				this.collectItems();
				this.currentIndex = this.items.findIndex((item) => item.src === target.dataset.lightbox);
				this.open();
			}
		});

		document.addEventListener('keydown', (e) => {
			if (!this.lightboxEl) {
				return;
			}
			if (e.key === 'Escape') {
				this.close();
			}
			if (e.key === 'ArrowRight') {
				this.next();
			}
			if (e.key === 'ArrowLeft') {
				this.prev();
			}
		});
	}

	private collectItems() {
		const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-lightbox]'));
		this.items = nodes.map((n) => {
			const src = n.dataset.lightbox!;
			const title = n.dataset.title ?? '';
			let type: LightboxItem['type'] = 'image';

			if (n.dataset.type === 'video') {
				type = 'video';
			} else if (n.dataset.type === 'iframe') {
				type = 'iframe';
			} else if (src.match(/\.(mp4|webm|ogg)$/i)) {
				type = 'video';
			} else if (src.startsWith('http') && src.includes('youtube')) {
				type = 'iframe';
			}

			return {
				src,
				title,
				type,
			};
		});
	}

	private open() {
		this.lightboxEl = document.createElement('div');
		this.lightboxEl.className = 'lightbox';
		this.lightboxEl.innerHTML = `
			<div class="lightbox__content">
				<button class="lightbox__close" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52" fill="none">
                        <path d="M39 13L13 39M13 13L39 39" stroke="currentColor" stroke-width="3" stroke-linecap="square" stroke-linejoin="round"/>
                    </svg>
                </button>
                <div class="lightbox__controls">
                    <button class="lightbox__navigation lightbox__prev" aria-label="Previous">
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="26" viewBox="0 0 36 26" fill="none">
                            <path d="M36 13L2.00016 13" stroke="currentColor" stroke-width="3"/>
                            <path d="M12.3916 25L1.99997 13.0001L12.3916 1" stroke="currentColor" stroke-width="3"/>
                        </svg>
                    </button>
                    <button class="lightbox__navigation lightbox__next" aria-label="Next">
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="26" viewBox="0 0 36 26" fill="none">
                            <path d="M0 13L33.9998 13" stroke="currentColor" stroke-width="3"/>
                            <path d="M23.6084 25L34 13.0001L23.6084 1" stroke="currentColor" stroke-width="3"/>
                        </svg>
                    </button>
                </div>
				<div class="lightbox__slide"></div>
			</div>
		`;
		document.body.appendChild(this.lightboxEl);

		this.lightboxEl.querySelector('.lightbox__close')?.addEventListener('click', () => this.close());
		this.lightboxEl.querySelector('.lightbox__prev')?.addEventListener('click', () => this.prev());
		this.lightboxEl.querySelector('.lightbox__next')?.addEventListener('click', () => this.next());
		document.body.classList.add('no-body-scrolling');

		this.lightboxEl.addEventListener('click', (e) => {
			if (e.target === this.lightboxEl) {
				this.close();
			}
		});

		this.show(this.currentIndex);
	}

	private show(index: number) {
		if (!this.lightboxEl) {
			return;
		}
		const len = this.items.length;
		this.currentIndex = ((index % len) + len) % len;

		const slide = this.lightboxEl.querySelector('.lightbox__slide') as HTMLElement;
		const item = this.items[this.currentIndex];
		const controls = this.lightboxEl.querySelector('.lightbox__controls') as HTMLElement;

		if (item.type === 'video' || item.type === 'iframe') {
			controls.classList.add('is-hidden');
		} else {
			controls.classList.remove('is-hidden');
		}

		if (item.type === 'video') {
			slide.innerHTML = `
			<div class="lightbox__slide-inner">
				<video src="${item.src}" controls autoplay></video>
				<span class="lightbox__title">${item.title ?? ''}</span>
			</div>
		`;
		} else if (item.type === 'iframe') {
			slide.innerHTML = `
			<div class="lightbox__slide-inner">
				<iframe src="${item.src}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
			</div>
			<span class="lightbox__title">${item.title ?? ''}</span>
		`;
		} else {
			slide.innerHTML = `
			<div class="lightbox__slide-inner">
				<img src="${item.src}" alt="" />
				<span class="lightbox__title">${item.title ?? ''}</span>
			</div>
		`;
		}

		slide.classList.remove('is-active');
		void slide.offsetWidth;
		slide.classList.add('is-active');
	}

	private close() {
		if (this.lightboxEl) {
			const video = this.lightboxEl.querySelector('video') as HTMLVideoElement | null;
			if (video) {
				video.pause();
			}
			const iframe = this.lightboxEl.querySelector('iframe') as HTMLIFrameElement | null;
			if (iframe) {
				iframe.src = '';
			}
			document.body.removeChild(this.lightboxEl);
			document.body.classList.remove('no-body-scrolling');
			this.lightboxEl = null;
		}
	}

	private next() {
		if (this.items[this.currentIndex].type === 'image') {
			this.show(this.currentIndex + 1);
		}
	}

	private prev() {
		if (this.items[this.currentIndex].type === 'image') {
			this.show(this.currentIndex - 1);
		}
	}
}
